/**
 * 挂件埋点：widget_retention_show / widget_retention_click
 *
 * - show：WidgetHost 确认挂件「新进入可见列表」时
 * - click：Host 统一点击复弹时（整块主区域 click_content=main_area）
 *
 * widget_id 取自 ACTIVITY_DEFAULTS[type].widgetId；trigger_scene 由调用方传入
 * （show 时 resolveWidgetShowTrigger；click 时复用当次曝光写入的 trigger_scene）。
 * show 实际上报成功后写入 widget-show-track-cache，供 Host 卸载回流后区分 trigger。
 */

import { useActivityStore } from '@/store/activity';
import { trackCustom, ETrackerEvents } from '@/utils/tracker';

import { resolveWidgetId } from '../defaults';

import { resolvePopupShowTabName } from './resolve-popup-show-track-params';
import { markWidgetShowTracked } from './widget-show-track-cache';

import type { TWidgetShowTrigger } from '../types';

/** 挂件主点击区域（整块挂件复弹） */
export const WIDGET_CLICK_CONTENT_MAIN = 'main_area' as const;

/**
 * 组装挂件埋点公共字段；未配置 widgetId 时返回 null（不上报）。
 * @param activityId 活动 id
 * @param triggerScene 本次曝光 / 点击对应的触发场景
 */
function buildWidgetRetentionBasePayload(
    activityId: string,
    triggerScene: TWidgetShowTrigger
): Record<string, string> | null {
    const activity = useActivityStore.getState().getActivity(activityId);
    const widgetId = activity?.type ? resolveWidgetId(activity.type) : undefined;
    if (!widgetId) {
        return null;
    }

    const payload: Record<string, string> = {
        widget_id: widgetId,
        trigger_scene: triggerScene,
    };
    const tabName = resolvePopupShowTabName();
    if (tabName) {
        payload.tab_name = tabName;
    }
    return payload;
}

/**
 * 上报挂件曝光。
 * @param opts.activityId 活动 id（用于解析 type → widget_id）
 * @param opts.triggerScene 本次曝光触发场景：cold_start / tab_switch
 */
export function trackWidgetRetentionShow(opts: { activityId: string; triggerScene: TWidgetShowTrigger }): void {
    const payload = buildWidgetRetentionBasePayload(opts.activityId, opts.triggerScene);
    if (!payload) {
        return;
    }
    trackCustom(ETrackerEvents.WIDGET_RETENTION_SHOW, payload);
    // 实际上报成功后再记，供 Host 卸载回流后区分 cold_start / tab_switch
    markWidgetShowTracked(opts.activityId);
}

/**
 * 上报挂件点击（整块主区域）。
 * @param opts.activityId 活动 id
 * @param opts.triggerScene 与当次曝光一致的触发场景
 * @param opts.clickContent 点击区域；默认 main_area
 */
export function trackWidgetRetentionClick(opts: {
    activityId: string;
    triggerScene: TWidgetShowTrigger;
    clickContent?: string;
}): void {
    const payload = buildWidgetRetentionBasePayload(opts.activityId, opts.triggerScene);
    if (!payload) {
        return;
    }
    payload.click_content = opts.clickContent ?? WIDGET_CLICK_CONTENT_MAIN;
    trackCustom(ETrackerEvents.WIDGET_RETENTION_CLICK, payload);
}
